import { useState } from "react";
import { useSelector } from "react-redux";

import { Divider, Tabs } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ClientPostpartum from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/ClientPostpartum/ClientPostpartum";
import NewbornPostpartum from "@/encounters/components/EncounterLabor/EncounterLaborPostpartum/NewbornPostpartum/NewbornPostpartum";
import { useBirthObservations } from "@/encounters/components/EncounterLabor/hooks/useBirthObservations";
import { selectBirthDateTime, selectIsOptimisticBirth } from "@/store/slices/birth";

const getTabStyle = (isActive: boolean) => ({
  background: isActive ? "var(--mantine-color-burgundy-9)" : "var(--mantine-color-cream-3)",
  borderRadius: "16px 16px 0 0",
});

const getTextColor = (isActive: boolean) => (isActive ? "white" : "var(--mantine-color-dark-9)");

function TabsList({ activeTab }: { activeTab: string | null }) {
  const isActive = (value: string) => activeTab === value;

  return (
    <Tabs.List grow>
      {[
        { label: "Client", value: "client-postpartum-flowsheet" },
        { label: "Newborn", value: "newborn-postpartum-flowsheet" },
      ].map(({ label, value }) => (
        <Tabs.Tab
          key={value}
          value={value}
          style={getTabStyle(isActive(value))}
          aria-label={`${label} Postpartum Flowsheet`}
          data-testid={`postpartum-tab-${label.toLowerCase()}`}
        >
          <TextWrapper size="sm" variant="bold" style={{ color: getTextColor(isActive(value)) }}>
            {label}
          </TextWrapper>
        </Tabs.Tab>
      ))}
    </Tabs.List>
  );
}

export default function PostpartumFlowsheetTabs({
  encounterId,
  clientId,
  isViewOnly,
}: {
  encounterId: string;
  clientId: string;
  isViewOnly: boolean;
}) {
  const birthDateTime = useSelector(selectBirthDateTime);
  const isOptimistic = useSelector(selectIsOptimisticBirth);
  const { setOptimisticBirth } = useBirthObservations(encounterId, clientId);

  const [activeTab, setActiveTab] = useState<string>("client-postpartum-flowsheet");

  // Allow rendering if we have either a real or optimistic birth date/time
  if (!birthDateTime && !isOptimistic) return null;

  return (
    <>
      <Tabs
        value={activeTab}
        variant="unstyled"
        data-testid="postpartum-flowsheet-tabs"
        onChange={(value) => setActiveTab(value ?? "client-postpartum-flowsheet")}
      >
        <TabsList activeTab={activeTab} />
        <Tabs.Panel value="client-postpartum-flowsheet">
          <ClientPostpartum
            clientId={clientId}
            isViewOnly={isViewOnly}
            encounterId={encounterId}
            onBirthCreate={setOptimisticBirth}
          />
        </Tabs.Panel>
        <Tabs.Panel value="newborn-postpartum-flowsheet">
          <NewbornPostpartum
            clientId={clientId}
            isViewOnly={isViewOnly}
            encounterId={encounterId}
            onBirthCreate={setOptimisticBirth}
          />
        </Tabs.Panel>
      </Tabs>
      <Divider my="xl" style={{ border: "1px solid var(--mantine-color-cream-4)" }} />
    </>
  );
}
